import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  DoctorHospital,
  invalidateAllBffApiTags,
  useDoctorHospitalsQuery,
  useSetActiveDoctorHospitalMutation
} from '@/modules';
import {
  Avatar,
  AvatarProps,
  Button,
  CircularProgress,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Typography
} from '@mui/material';
import { useCurrentHospital } from '@/modules/appContext';
import { SentryService } from '@ianesthesie-kit/sentry';
import { useAppDispatch } from '@/hooks';
import { useDelayedValue } from '@ianesthesie-kit/delay';
import { themeColors } from '@ianesthesie-kit/config';
import { motion } from 'framer-motion';
import { CenteredContainer, TranslatedText } from '@ianesthesie-kit/core';

const HospitalLogo = ({
  doctorHospital,
  sx,
  ...props
}: AvatarProps & {
  doctorHospital: DoctorHospital | undefined;
}) => (
  <Avatar
    src={doctorHospital?.hospital.logoPath}
    sx={{
      bgcolor: doctorHospital?.active ? themeColors.blue[500] : undefined,
      border: doctorHospital?.active
        ? `4px solid ${themeColors.green[500]}`
        : undefined,
      ...sx
    }}
    {...props}
  >
    {doctorHospital?.hospital.name.slice(0, 1)}
  </Avatar>
);

export const ActiveHospitalSelect = () => {
  const [buttonAnchor, setButtonAnchor] = useState<null | HTMLElement>(null);
  const dispatch = useAppDispatch();
  const { data: availableDoctorHospitals, isFetching: isFetchnigList } =
    useDoctorHospitalsQuery();
  const {
    refresh,
    isUnstable: isLoadingActive,
    queryResult: { data: currentHospital }
  } = useCurrentHospital({ skip: (availableDoctorHospitals?.length ?? 0) < 2 });
  const [setActiveHospital, { isLoading: isMutating }] =
    useSetActiveDoctorHospitalMutation();

  const activeDoctorHospital = useMemo(
    () => availableDoctorHospitals?.find((dh) => dh.active),
    [availableDoctorHospitals]
  );

  useEffect(() => {
    if (activeDoctorHospital?.id) {
      refresh();
    }
  }, [activeDoctorHospital?.id, refresh]);

  const [delayedActiveHospital, delayExpired] =
    useDelayedValue(activeDoctorHospital);

  const loading = isFetchnigList || isLoadingActive || isMutating;

  const onClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      const doctorHospitalId = e.currentTarget.dataset.dhid;
      if (doctorHospitalId) {
        setActiveHospital(doctorHospitalId)
          .then((tokenRefreshed) => {
            setButtonAnchor(null);
            if (tokenRefreshed) {
              dispatch(invalidateAllBffApiTags());
              refresh();
            }
          })
          .catch((err) => SentryService.getInstance().captureException(err));
      }
    },
    [dispatch, refresh, setActiveHospital]
  );

  if ((availableDoctorHospitals?.length ?? 0) < 2 || !currentHospital) {
    return null;
  }

  return (
    <>
      <Button
        layout
        component={motion.button}
        variant="outlined"
        color="riskNone"
        disabled={!delayExpired || loading}
        style={{
          position: 'relative',
          transformOrigin: 'center',
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }}
        onClick={(e: MouseEvent<HTMLButtonElement>) =>
          setButtonAnchor(e.currentTarget)
        }
        startIcon={
          <HospitalLogo
            sx={{ width: 24, height: 24, border: 'none' }}
            doctorHospital={delayedActiveHospital}
          />
        }
      >
        {delayedActiveHospital?.hospital.name}
        {(!delayExpired || loading) && (
          <CenteredContainer
            position="absolute"
            bgcolor={'rgba(255 255 255 / 0.7)'}
          >
            <CircularProgress size={20} thickness={5} />
          </CenteredContainer>
        )}
      </Button>
      <Menu
        onClose={() => setButtonAnchor(null)}
        open={!!buttonAnchor}
        anchorEl={buttonAnchor}
      >
        <Typography
          px={2}
          py={1}
          fontSize="large"
          color="text.secondary"
          fontWeight={500}
        >
          <TranslatedText id="doctor-dashboard.activeHospitalMenu.title" />
        </Typography>
        <Divider sx={{ mb: 1 }} />
        {availableDoctorHospitals?.map((doctorHospital) => (
          <ListItemButton
            key={doctorHospital.id}
            disabled={loading}
            data-dhid={doctorHospital.id}
            onClick={onClick}
            sx={{
              pointerEvents: doctorHospital.active ? 'none' : 'all',
              background: doctorHospital.active
                ? themeColors.indigo[100]
                : undefined
            }}
          >
            <ListItemIcon>
              <HospitalLogo doctorHospital={doctorHospital} />
            </ListItemIcon>
            <ListItemText
              primary={doctorHospital.hospital.name}
              primaryTypographyProps={{
                fontWeight: doctorHospital.active ? 700 : 500
              }}
            />
          </ListItemButton>
        ))}
      </Menu>
    </>
  );
};
